import { useLocation } from "react-router-dom";
import { listOrders, type AdminOrderRow, type OrderSource } from "@/api/admin";
import { useAsync } from "@/lib/useAsync";
import { useUrlParam } from "@/lib/useUrlParam";
import { money, when } from "@/lib/format";
import { OrderLink } from "@/components/EntityLink";
import { FilterChips } from "@/components/FilterChips";
import { PagedList, usePagedCursor } from "@/components/ui/paged-list";
import { SearchBox, useUrlSearch } from "@/components/SearchBox";
import { Badge, type BadgeTone } from "@/components/ui/badge";
import { PageHeader } from "@/components/ui/page-header";
import { TD, TH, TR } from "@/components/ui/table";

/**
 * The ledger, with the operator's own vocabulary in it.
 *
 * THESE STATUSES ARE NOT THE CUSTOMER'S. `on_hold` and `paid_pending_provision`
 * are folded into "provisioning" on the storefront, because a customer has no
 * use for the difference; the operator is the person those two words exist for,
 * and a screen that could not tell a held order from one the supplier has not
 * answered about would be a screen nobody could act on.
 */
const STATUS_TONE: Record<string, BadgeTone> = {
  created: "gray",
  on_hold: "amber",
  paid_pending_provision: "blue",
  provisioning: "blue",
  provisioned: "green",
  failed: "red",
  refunded: "gray",
};

/** The word an operator uses, not the column value. */
const STATUS_LABEL: Record<string, string> = {
  created: "Awaiting payment",
  on_hold: "Held for approval",
  paid_pending_provision: "Waiting on supplier",
  provisioning: "Delivering",
  provisioned: "Delivered",
  failed: "Failed",
  refunded: "Refunded",
};

export function statusBadge(status: string) {
  return <Badge color={STATUS_TONE[status] ?? "gray"}>{STATUS_LABEL[status] ?? status}</Badge>;
}

/** Where an order was placed, in the operator's words. */
const SOURCE_LABEL: Record<OrderSource, string> = { web: "Web", telegram: "Telegram" };
const SOURCE_TONE: Record<OrderSource, BadgeTone> = { web: "gray", telegram: "blue" };

export function sourceBadge(source: OrderSource) {
  return <Badge color={SOURCE_TONE[source]}>{SOURCE_LABEL[source]}</Badge>;
}

const SOURCES: { value: string; label: string }[] = [
  { value: "", label: "Web and Telegram" },
  { value: "web", label: SOURCE_LABEL.web },
  { value: "telegram", label: SOURCE_LABEL.telegram },
];

const FILTERS: { value: string; label: string }[] = [
  { value: "", label: "All" },
  { value: "created", label: "Awaiting payment" },
  { value: "on_hold", label: "Held" },
  { value: "paid_pending_provision", label: "Waiting on supplier" },
  { value: "failed", label: "Failed" },
  { value: "provisioned", label: "Delivered" },
  { value: "refunded", label: "Refunded" },
];

export function OrdersScreen() {
  // THE FILTER LIVES IN THE URL. An operator who finds a held order and sends
  // the link to a colleague has to send the same screen, and a back button that
  // forgets the filter is a back button nobody trusts.
  const [status, setStatus] = useUrlParam("status");
  const [source, setSource] = useUrlParam("source");
  // The worker reads the code: dashes and look-alikes are its rule.
  const search = useUrlSearch("code");
  const code = search.value;

  // Read again on every visit, clicking Orders while already here included:
  // that click is how an operator asks for the orders the menu says are new,
  // and they are on the first page, so a visit starts there.
  const visit = useLocation().key;

  // A filter and a search change the URL, so they are visits too.
  const pager = usePagedCursor(visit);
  const { cursor } = pager;
  const { data, loading, error } = useAsync(
    () => listOrders({ status, code, source, cursor }),
    [status, code, source, cursor, visit],
  );

  return (
    <div className="space-y-4">
      <PageHeader title="Orders" description="Every order, with the statuses a customer never sees.">
        <SearchBox
          search={search}
          className="w-40"
          placeholder="Order code"
          label="Find an order by its code"
        />
      </PageHeader>

      <div className="flex flex-wrap gap-1.5">
        <FilterChips options={FILTERS} value={status} onChange={setStatus} />
      </div>
      <div className="flex flex-wrap gap-1.5">
        <FilterChips options={SOURCES} value={source} onChange={setSource} />
      </div>

      <PagedList
        head={
          <>
            <TH>Code</TH>
            <TH>Status</TH>
            <TH>Package</TH>
            <TH numeric>Qty</TH>
            <TH numeric>Total</TH>
            <TH>Customer</TH>
            <TH>Source</TH>
            <TH>Created</TH>
          </>
        }
        rows={data?.rows ?? []}
        renderRow={(order) => <OrderRow key={order.id} order={order} />}
        columns={8}
        loading={loading}
        error={error}
        empty={code || status || source ? "No order matches that filter." : "No orders yet."}
        pager={pager}
        nextCursor={data?.nextCursor}
      />
    </div>
  );
}

function OrderRow({ order }: { order: AdminOrderRow }) {
  return (
    <TR>
      <TD>
        <OrderLink code={order.order_code} />
      </TD>
      <TD>{statusBadge(order.status)}</TD>
      <TD className="text-secondary">{order.package_id}</TD>
      <TD numeric>{order.quantity}</TD>
      <TD numeric>{money(order.total_cents)}</TD>
      <TD className="max-w-[220px] truncate text-secondary">{order.email ?? "—"}</TD>
      <TD>
        {sourceBadge(order.source)}
      </TD>
      <TD className="whitespace-nowrap text-muted-foreground">{when(order.created_at)}</TD>
    </TR>
  );
}
